import clsx from "clsx";
import type { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import { PERSONA_LABELS, type GuardianType } from "@/services/api/guardiansv2/types";
import { useGuardiansAuditLaunch } from "../auditLaunch/GuardiansAuditLaunchContext";
import type { AuditPersonaTabId } from "./AuditPersonaTabs";
import { personasFrom } from "./auditNavigation";

export type PersonaRerunAction = "single" | "cascade" | "synthesis" | "full";

export interface AuditPersonaRerunActionsProps {
  auditId: string;
  personaTab: AuditPersonaTabId;
  disabled?: boolean;
  activeAction: PersonaRerunAction | null;
  onActionStart: (action: PersonaRerunAction) => void;
  onActionEnd: () => void;
  onComplete?: () => void;
}

const iconButtonClass =
  "relative rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200 disabled:opacity-50";

interface RerunActionButtonProps {
  tooltip: string;
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
  "aria-label": string;
  "data-testid": string;
  children: ReactNode;
}

function RerunActionButton({
  tooltip,
  disabled,
  busy,
  onClick,
  children,
  ...rest
}: RerunActionButtonProps) {
  return (
    <Tooltip content={tooltip} placement="top">
      <button
        type="button"
        disabled={disabled || busy}
        onClick={onClick}
        className={iconButtonClass}
        {...rest}
      >
        {children}
      </button>
    </Tooltip>
  );
}

type RerunOverlay = "1" | "cascade";

const OVERLAY_MARK: Record<Exclude<RerunOverlay, never>, string> = {
  "1": "1",
  cascade: "›",
};

function RerunRotateIcon({
  busy,
  overlay,
}: {
  busy: boolean;
  overlay?: RerunOverlay;
}) {
  return (
    <span
      className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center text-current"
      aria-hidden
    >
      <FontAwesomeIcon
        icon={busy ? faSpinner : faRotateRight}
        className={clsx("h-9 w-9", busy && "animate-spin")}
      />
      {!busy && overlay ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[7px] font-bold leading-none">
          {OVERLAY_MARK[overlay]}
        </span>
      ) : null}
    </span>
  );
}

/** Per-tab rerun controls (single persona, cascade, or synthesis-only). */
export function AuditPersonaRerunActions({
  auditId,
  personaTab,
  disabled = false,
  activeAction,
  onActionStart,
  onActionEnd,
  onComplete,
}: AuditPersonaRerunActionsProps) {
  const { registerLaunches } = useGuardiansAuditLaunch();

  const run = async (
    action: PersonaRerunAction,
    personas?: GuardianType[] | null
  ) => {
    onActionStart(action);
    try {
      const result =
        action === "full"
          ? await GuardiansV2API.rerunAudit(auditId)
          : action === "synthesis"
            ? await GuardiansV2API.rerunAudit(auditId, [])
            : await GuardiansV2API.rerunAudit(auditId, personas ?? null);
      registerLaunches([
        {
          taskId: result.task_id,
          auditId: result.audit_id,
          kind: "rerun",
        },
      ]);
      onComplete?.();
    } finally {
      onActionEnd();
    }
  };

  if (personaTab === "synthesis") {
    const synthesisBusy = activeAction === "synthesis";
    const fullBusy = activeAction === "full";
    const anyBusy = synthesisBusy || fullBusy;

    return (
      <div className="flex shrink-0 items-center gap-0.5" data-testid="audit-persona-rerun-actions">
        <RerunActionButton
          tooltip="Re-run synthesis only"
          disabled={disabled}
          busy={anyBusy}
          onClick={() => void run("synthesis")}
          aria-label="Re-run synthesis only"
          data-testid="audit-rerun-synthesis"
        >
          <RerunRotateIcon busy={synthesisBusy} overlay="1" />
        </RerunActionButton>
        <RerunActionButton
          tooltip="Re-run entire audit"
          disabled={disabled}
          busy={anyBusy}
          onClick={() => void run("full")}
          aria-label="Re-run entire audit"
          data-testid="audit-rerun-full-audit"
        >
          <RerunRotateIcon busy={fullBusy} overlay="cascade" />
        </RerunActionButton>
      </div>
    );
  }

  const persona = personaTab;
  const personaLabel = PERSONA_LABELS[persona];
  const singleBusy = activeAction === "single";
  const cascadeBusy = activeAction === "cascade";

  return (
    <div className="flex shrink-0 items-center gap-0.5" data-testid="audit-persona-rerun-actions">
      <RerunActionButton
        tooltip={`Re-run ${personaLabel} only`}
        disabled={disabled}
        busy={singleBusy || cascadeBusy}
        onClick={() => void run("single", [persona])}
        aria-label={`Re-run ${personaLabel} only`}
        data-testid="audit-rerun-single-persona"
      >
        <RerunRotateIcon busy={singleBusy} overlay="1" />
      </RerunActionButton>
      <RerunActionButton
        tooltip={`Re-run ${personaLabel} through synthesis`}
        disabled={disabled}
        busy={singleBusy || cascadeBusy}
        onClick={() => void run("cascade", personasFrom(persona))}
        aria-label={`Re-run ${personaLabel} and later Guardians`}
        data-testid="audit-rerun-cascade-personas"
      >
        <RerunRotateIcon busy={cascadeBusy} overlay="cascade" />
      </RerunActionButton>
    </div>
  );
}
